import {
  Fragment,
  createRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import dataConstants from "../../constants/dataConstants";
import useCreateOrder from "../../services/utils/custom hooks/OnlineOrder/useCreateOrder";
import { ApplicationState } from "../../services/context/ApplicationStateContext";
import OnlineOrderValuesModel from "../../services/models/formModels/onlineOrderValues.model";
import OnlineOrderActions from "../../services/OnlineOrderActions";
import { PopUpContext } from "../../services/context/popupContext";
import OnlineOrderSelectionFlow from "../../components/popups/onlineOrderSelectionFlowPopup";
import useProfile from "../../services/utils/custom hooks/useProfile";
import OnlineOrderForm from "../../components/global/forms/OnlineOrderForm";
import { useHistory, useLocation } from "react-router-dom";
import { useOktaAuth } from "@okta/okta-react";
import usePathLabs from "../../services/utils/custom hooks/usePathLabs";
import { OrderStepsContext } from "../../services/context/OrderStepsContext";
import { OrderStepActions } from "../../services/reducers/OrderStepsReducer";
import { useAdminContext } from "../../services/context/TourPopupContext";

function OnlineOrderPage(): JSX.Element {
  const [shouldValidate, setShouldValidate] = useState<boolean>(false);
  const { state }: any = useLocation();
  const orderValue = state?.orderValue?.data;
  const patientInformation = state?.patientInformation;
  const profile = useProfile();
  const { authState }: any = useOktaAuth();
  const {
    state: { selectedOrganization, selectedPhysician, selectedPhysicianData, selectedPathLab },
  } = useContext(ApplicationState);
  const roles = profile?.data?.user?.roles ?? [];
  const { setVisible, setPopUp } = useContext(PopUpContext);
  const [initialValues, setInitialValues] = useState<OnlineOrderValuesModel>({
    ...dataConstants.onlineOrderInitialValues,
    isPhysicianSignPermission: false,
    organizationId: selectedOrganization?.toString?.() ?? "",
    physicianId: selectedPhysician?.toString?.() ?? "",
    additionalClinician: [],
    isTissueShipped: false,
    shipTracking: "",
    shipMethod: "",
    laboratoryInfo: {
      labortaryName: "",
      labortaryId: "",
      dateOfCollection: "",
      FacilityContactPerson: "",
      labortaryPhone: "",
      labortaryFax: "",
      specimenId: "",
      typeOfSpecimen: "",
      medicarePullDate: "",
    },
    patientId: "",
    patientInfo: {
      firstName: "",
      lastName: "",
      gender: "",
      address: "",
      phone: "",
      middleInitial: "",
      birthDate: "",
      email: "",
      securityInfo: {
        ssn: "",
        mrn: "",
      },
      city: "",
      state: "",
      zipcode: "",
    },
  });
  const { data: pathLabs, isLoading } = usePathLabs();

  const { mutate, isPending } = useCreateOrder();
  const { dispatch } = useContext(OrderStepsContext);
  const [preventDiscardPopup, setPreventDiscardPopup] =
    useState<boolean>(false);
  const history = useHistory<any>();
  const formRef = useRef<any>();
  const { isTourActive } = useAdminContext();

  useEffect(() => {
    const orderActions = new OnlineOrderActions(
      selectedOrganization,
      selectedPhysician,
      selectedPhysicianData,
      roles,
      profile?.data?.user?.id,
      selectedPathLab,
      undefined,
      authState?.accessToken?.accessToken ?? ""
    );

    if (orderValue) {
      setInitialValues(orderValue);
      setShouldValidate(true);
    }

    if (patientInformation) {
      const parsed = JSON.parse(patientInformation);
      const patientIdFromState = parsed?.id ?? "";
      const patientData = { ...parsed };
      delete patientData.id;
      setShouldValidate(true);
      setInitialValues((prevState) => ({
        ...prevState,
        patientId: patientIdFromState,
        patientInfo: {
          ...prevState.patientInfo,
          ...patientData,
        },
      }));
    }

    if (history?.location?.state?.tourPopup) {
      setPreventDiscardPopup(true);
      let element = document.getElementById("step6");
      element?.classList.add("content-highlight");
      element?.classList.add("mx-[80px]");
    }

    orderActions.allowedToCreateOrder().then((res) => {
      if (!res && !isTourActive) {
        setVisible(true);
        setPopUp(<OnlineOrderSelectionFlow orderActions={orderActions} />);
        setPreventDiscardPopup(true);
      } else {
        setPreventDiscardPopup(false);
        if (roles.includes("Collaborative Order Agreement")) {
          dispatch({
            type: OrderStepActions.DISABLE_FIELD,
            payload: "laboratoryInfo.labortaryName",
          });
          setInitialValues((prevState) => ({
            ...prevState,
            laboratoryInfo: {
              ...prevState.laboratoryInfo,
              labortaryId: selectedPathLab ? selectedPathLab : "",
              labortaryName:
                pathLabs?.data.find((pathLab) => pathLab.id === selectedPathLab)
                  ?.name ?? "",
            },
          }));
        }
      }
    });
  }, [selectedOrganization, selectedPhysician, selectedPathLab, patientInformation]);

  useEffect(() => {
    return () => {
      sessionStorage.removeItem("orderDuplicatePatientId");
    };
  }, []);

  return (
    <OnlineOrderForm
      ref={formRef}
      preventDiscardPopup={preventDiscardPopup}
      setPreventDiscardPopup={setPreventDiscardPopup}
      initialValues={initialValues}
      isPending={isPending}
      mutate={mutate}
      isUpdate={false}
      shouldValidate={shouldValidate}
    />
  );
}

export default OnlineOrderPage;
